import PropTypes from "prop-types";
import { cloneElement, Component, isValidElement } from "react";
import styled from "styled-components";
import ReactDOM from "react-dom";

import {
    Manager,
    Reference,
    Popper
} from "react-popper";

import { domId } from "common/utils";

export class Tooltip extends Component {
    static defaultProps = {
        position: "top",
        skipWrapper: false,
        containerDisplayMode: "inline-block"
    };
    
    static propTypes = {
        skipWrapper: PropTypes.bool,
        title: PropTypes.string.isRequired,
        containerDisplayMode: PropTypes.string,
        position: PropTypes.oneOf([
            "bottom",
            "top",
            "left",
            "right",
            "bottom-start",
            "bottom-end",
            "top-start",
            "top-end",
            "left-start",
            "left-end",
            "right-start",
            "right-end",
            "auto"
        ])
    };
    
    constructor(props) {
        super(props);
        
        let portal = document.getElementById("tooltip-portal");
        if (!portal) {
            portal = document.createElement("div");
            portal.setAttribute("id", "tooltip-portal");
            document.body.appendChild(portal);
        }
        
        this.portalEl = portal;
        this.state = {
            isOpen: false
        };
        this.tooltipId = domId("tooltip-");
    }
    
    setOpen = () => {
        this.setState({ isOpen: true });
    };
    
    handleOpen = () => {
        if (this.props.delay) {
            this.delayTimeout = window.setTimeout(this.setOpen, this.props.delay);
        } else {
            this.setOpen();
        }
    };
    
    handleClose = () => {
        this.setState({ isOpen: false });
        
        if (this.delayTimeout) {
            window.clearTimeout(this.delayTimeout);
            this.delayTimeout = null;
        }
    };
    
    renderTrigger(children, ref) {
        const propList = {
            "aria-describedby": this.tooltipId,
            onFocus: this.handleOpen,
            onBlur: this.handleClose,
            onMouseEnter: this.handleOpen,
            onMouseLeave: this.handleClose
        };
        
        // Use the `type` property of the react instance to detect whether we
        // have a basic element (type=string) or a class/function component (type=function)
        // Because we can't rely on the child element implementing forwardRefs we wrap
        // it with a span tag so that popper has ref
        if (isValidElement(children) && (this.props.skipWrapper || typeof children.type === "string")) {
            // Basic DOM nodes can be cloned and have more props applied.
            return cloneElement(children, {
                ...propList,
                ref
            });
        }
    
        propList.containerDisplayMode = this.props.containerDisplayMode;
        return (
            <Container {...propList} className={this.props.className} ref={ref}>
                {children}
            </Container>
        );
    }
    
    render() {
        if (this.props.disabled || this.props.title === "") {
            return this.props.children;
        }
    
        const modifiers = [
            {
                name: "hide",
                enabled: false
            },
            {
                name: "preventOverflow",
                options: {
                    padding: 10,
                    rootBoundary: "viewport" // TODO: do we need it here. viewport is default value
                }
            },
            {
                name: "offset",
                options: {
                    offset: [0, 6]
                }
            }
        ];
    
        let tip = null;
        if (this.state.isOpen) {
            tip = ReactDOM.createPortal(
                <Popper placement={this.props.position} modifiers={modifiers}>
                    {({ ref, style, placement, arrowProps }) => (
                        <TooltipContent
                            id={this.tooltipId}
                            aria-hidden={!this.state.isOpen}
                            ref={ref}
                            style={style}
                            data-placement={placement}
                        >
                            {this.props.title}
                            <TooltipArrow
                                ref={arrowProps.ref}
                                data-placement={placement}
                                style={arrowProps.style}
                            />
                        </TooltipContent>
                    )}
                </Popper>,
                this.portalEl
            );
        }
        
        return (
            <Manager>
                <Reference>{({ ref }) => this.renderTrigger(this.props.children, ref)}</Reference>
                {tip}
            </Manager>
        );
    }
}

export const withTooltip = (component, tooltipProps) => <Tooltip {...tooltipProps}>{component}</Tooltip>;

const TooltipContent = styled("div")`
    color: #fff;
    background: #000;
    opacity: 0.9;
    padding: 5px 10px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    border-radius: 2px;
    overflow-wrap: break-word;
    max-width: 225px;
    z-index: 10002;
    
    font-weight: bold;
    font-size: 12px;
    line-height: 1.4;
    
    text-align: center;
`;

const TooltipArrow = styled("span")`
    position: absolute;
    width: 10px;
    height: 5px;
    
    &[data-placement*='bottom'] {
        top: 0;
        left: 0;
        margin-top: -8px;
        &:before {
            border-width: 0 5px 5px 5px;
            border-color: transparent transparent #000 transparent;
        }
    }
    
    &[data-placement*='top'] {
        bottom: 0;
        left: 0;
        margin-bottom: -3px;
        &:before {
            border-width: 5px 5px 0 5px;
            border-color: #000 transparent transparent transparent;
        }
    }
    
    &[data-placement*='right'] {
        left: 0;
        margin-left: -8px;
        margin-top: -5px;
        &:before {
            border-width: 5px 5px 5px 0;
            border-color: transparent #000 transparent transparent;
        }
    }
    
    &[data-placement*='left'] {
        right: 0;
        margin-right: -5px;
        &:before {
            border-width: 5px 0 5px 5px;
            border-color: transparent transparent transparent #000;
        }
    }
    
    &:before {
        content: '';
        margin: auto;
        display: block;
        width: 0;
        height: 0;
        border-style: solid;
    }
`;

// Using an inline-block solves the container being smaller
// than the elements it is wrapping
const Container = styled.span`
    display: ${(props) => props.containerDisplayMode};
    max-width: 100%;
`;
